import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ByWeekday, RRule, Weekday } from 'rrule';
import { DaysOfWeek } from '@frontend/design-system';
import {
  AvailabilityRecurrenceTypeOptions,
  AvailabilityRecurrenceTypes,
  MonthlyFrequencyTypeOptions,
  MonthlyRecurrenceFormValues,
  MonthlyWeekDayOptions,
  RepeatFrequencyOptions,
  RuleRecurrenceFormValues,
} from './components/availability/availability-form/types';

dayjs.extend(utc);

const dayMap = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
} as const;

export type DayMapKey = keyof typeof dayMap;

const daysMap = {
  monday: 'MO',
  tuesday: 'TU',
  wednesday: 'WE',
  thursday: 'TH',
  friday: 'FR',
  saturday: 'SA',
  sunday: 'SU',
} as const;

type DaysMapKey = keyof typeof daysMap;

export const daysStringMap = {
  MO: 'monday',
  TU: 'tuesday',
  WE: 'wednesday',
  TH: 'thursday',
  FR: 'friday',
  SA: 'saturday',
  SU: 'sunday',
} as const;

const daysNumberMap = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const weekNumberString: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
} as const;

const isDayAbbreviated = (day: DaysMapKey | DayMapKey): day is DayMapKey => {
  return Object.keys(dayMap).includes(day);
};

/**
 * @desc: Function is defined to get the rrule day of the week (Weekday) based on the day abbreviation
 * @param day : string | number - day abbreviation or day number (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
 * @returns : Weekday
 */
export const getRRuleDay = (day: number | DaysMapKey | DayMapKey): Weekday => {
  const dayAbbrString = typeof day === 'number' ? daysNumberMap[day] : isDayAbbreviated(day) ? day : daysMap[day];
  return (dayAbbrString && dayMap[dayAbbrString]) || RRule.MO;
};

export const getRRuleTimeString = (time: string | undefined): string => {
  const [hours = '00', minutes = '00', seconds = '00'] = (time ?? '').split(':');
  return `${hours.padStart(2, '0')}${minutes.padStart(2, '0')}${seconds.padStart(2, '0')}`;
};

export const getRRuleDateString = (date: string | undefined): string => {
  return date ? dayjs(date).format('YYYYMMDD') : '';
};

export const formRRuleFromAvailabilityRuleFormValues = ({
  isAvailabilityRule,
  recurrenceType,
  recurrenceFormValues,
  monthlyRecurrenceFormValues,
  daysOfWeek,
  repeatOn,
}: {
  isAvailabilityRule?: boolean;
  recurrenceType: AvailabilityRecurrenceTypes | undefined;
  recurrenceFormValues: RuleRecurrenceFormValues;
  monthlyRecurrenceFormValues: MonthlyRecurrenceFormValues;
  daysOfWeek: Weekday[];
  repeatOn: Weekday[];
}) => {
  const { repeatFrequency, repeatFrequencyType } = recurrenceFormValues;

  if (recurrenceType === 'SPECIFIC_DATE') {
    return new RRule({
      freq: RRule.YEARLY,
    });
  }

  if (recurrenceType === 'WEEKLY') {
    return new RRule({
      freq: RRule.WEEKLY,
      byweekday: daysOfWeek,
    });
  }

  if (recurrenceType === 'CUSTOM' && repeatFrequency) {
    const customRRuleOptions = { interval: Number(repeatFrequency) };
    if (repeatFrequencyType === 'days') {
      return new RRule({
        freq: RRule.DAILY,
        ...customRRuleOptions,
      });
    } else if (repeatFrequencyType === 'weeks') {
      return new RRule({
        freq: RRule.WEEKLY,
        byweekday: repeatOn,
        ...customRRuleOptions,
      });
    } else if (repeatFrequencyType === 'months') {
      const { monthlyFrequencyType, monthlyFrequencyDay, monthlyFrequencyWeek, monthlyFrequencyWeekday } =
        monthlyRecurrenceFormValues;
      if (monthlyFrequencyType === 'day') {
        return new RRule({
          freq: RRule.MONTHLY,
          bymonthday: Number(monthlyFrequencyDay),
          ...customRRuleOptions,
        });
      } else if (monthlyFrequencyType === 'weekday') {
        if (!monthlyFrequencyWeekday || !monthlyFrequencyWeek) return;
        const weekMap: Record<string, number> = {
          first: 1,
          second: 2,
          third: 3,
          fourth: 4,
          last: -1,
        };
        const rruleWeekday = getRRuleDay(monthlyFrequencyWeekday as DaysMapKey).nth(weekMap[monthlyFrequencyWeek]);
        return new RRule({
          freq: RRule.MONTHLY,
          byweekday: rruleWeekday,
          ...customRRuleOptions,
        });
      }
    }
  }

  // NOTE: Currently, Availability Rule Create/Update API doesn't support empty rRule, so we are setting single occurrence rRule.
  // This can be removed once API is updated to support empty rRule field.
  if (isAvailabilityRule && recurrenceType === 'SINGLE_OCCURRENCE') {
    return new RRule({
      freq: RRule.YEARLY,
      count: 1,
    });
  }

  return;
};

export const getAvailabilityRecurrenceTypeFromRRule = (
  rRuleObj: RRule
): AvailabilityRecurrenceTypeOptions | undefined => {
  const options = rRuleObj.options;
  if (options.freq === RRule.YEARLY && options.count === 1) {
    // SINGLE_OCCURRENCE
    return;
  }
  if (options.freq === RRule.YEARLY && !options.byweekday && !options.bymonthday) {
    return 'SPECIFIC_DATE';
  }
  if (options.freq === RRule.WEEKLY && options.byweekday && !options.interval) {
    return 'WEEKLY';
  }
  if (
    options.freq === RRule.DAILY ||
    options.freq === RRule.MONTHLY ||
    (options.freq === RRule.WEEKLY && options.interval)
  ) {
    return 'CUSTOM';
  }
  return undefined;
};

export const getFrequencyValuesFromRRule = (rRuleObj: RRule | undefined) => {
  const { origOptions: rRuleOptions } = rRuleObj || {};
  const { freq, interval = 1, bymonthday, byweekday } = rRuleOptions || {};
  const frequency = interval?.toString();
  const frequencyType: RepeatFrequencyOptions =
    freq === RRule.WEEKLY ? 'weeks' : freq === RRule.MONTHLY ? 'months' : 'days';

  let monthlyFrequencyType: MonthlyFrequencyTypeOptions = 'day';
  let monthlyFrequencyDay = '1';
  let monthlyFrequencyWeek = 'first';
  let monthlyFrequencyWeekday: MonthlyWeekDayOptions = 'monday';
  const weekdays: Weekday[] = [];

  if (frequencyType === 'weeks') {
    const byweekdays = Array.isArray(byweekday) ? byweekday : byweekday ? [byweekday] : [];
    weekdays.push(
      ...byweekdays.map((day) => getWeekdayFromByWeekDay(day)[0]).filter((day): day is Weekday => day !== undefined)
    );
  } else if (frequencyType === 'months') {
    if (bymonthday) {
      const monthDay = Array.isArray(bymonthday) ? bymonthday[0] : bymonthday;
      monthlyFrequencyDay = monthDay.toString();
    } else if (byweekday) {
      monthlyFrequencyType = 'weekday';
      const firstByWeekday = Array.isArray(byweekday) ? byweekday[0] : byweekday;
      const [weekday, weekNumber] = getWeekdayFromByWeekDay(firstByWeekday);
      monthlyFrequencyWeekday = weekday ? daysStringMap[weekday.toString() as DayMapKey] : 'monday';
      monthlyFrequencyWeek = weekNumber !== undefined ? weekNumberString[weekNumber as number] : 'first';
    }
  }

  return {
    frequency,
    frequencyType,
    monthlyFrequencyType,
    monthlyFrequencyDay,
    monthlyFrequencyWeek,
    monthlyFrequencyWeekday,
    weekdays,
  };
};

export const getWeekdayFromByWeekDay = (
  byweekday: ByWeekday | undefined
): [Weekday | undefined, number | undefined] => {
  if (!byweekday) return [undefined, undefined];
  if (byweekday && typeof byweekday === 'object') {
    const weekdayIndex = byweekday.weekday; // 0..6 => MO..SU
    const weekNumber = byweekday.n; // 1,2,3,4,-1 (or undefined for plain weekly MO/WE)
    return [dayMap[daysNumberMap[weekdayIndex]], weekNumber];
  }
  if (typeof byweekday === 'number') {
    return [dayMap[daysNumberMap[byweekday]], undefined];
  }
  if (typeof byweekday === 'string') {
    const numString = Number(byweekday.slice(0, -2));
    const dayString = byweekday.slice(-2) as DayMapKey;
    return [dayMap[dayString], numString ? numString : undefined];
  }
  return [undefined, undefined]; // Default fallback
};

export const getRRuleDayFromDaysOfWeek = (day: DaysOfWeek): Weekday => getRRuleDay(day.toLowerCase() as DaysMapKey);

export const getDaysOfWeekFromRRuleDay = (rruleDay: Weekday): DaysOfWeek => {
  const dayString = daysStringMap[rruleDay.toString() as DayMapKey];
  return (dayString.charAt(0).toUpperCase() + dayString.slice(1)) as DaysOfWeek;
};
